/**
 * AddMemoryModal - Modal for adding manual memories to Knowledge Base
 * Modern UI matching Second Brain style with markdown preview
 */

import { useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import MicButton from "./MicButton";
import "../../assets/css/sb-modal.css";

export default function AddMemoryModal({ show, onClose, onSave }) {
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!content.trim()) {
      setError("Please enter a memory");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(content.trim());
      setContent("");
      setShowPreview(false);
    } catch {
      setError("Failed to save memory");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setContent("");
    setError(null);
    setShowPreview(false);
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="sb-modal">
      <Modal.Header closeButton className="sb-modal-header">
        <Modal.Title className="sb-modal-title">
          <i className="bi bi-lightbulb-fill me-2"></i>
          Add Memory
        </Modal.Title>
      </Modal.Header>
      
      <form onSubmit={handleSubmit}>
        <Modal.Body className="sb-modal-body">
          <p className="sb-description">
            Add a fact or piece of information you want Zoomey to remember about you.
          </p>

          {error && (
            <div className="sb-error-alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          {/* Content with Preview Toggle */}
          <div className="sb-form-group">
            <div className="sb-label-row">
              <label className="sb-label">Memory Content <span className="required">*</span></label>
              <button
                type="button"
                className={`sb-preview-toggle ${showPreview ? 'active' : ''}`}
                onClick={() => setShowPreview(!showPreview)}
              >
                <i className={`bi ${showPreview ? 'bi-pencil' : 'bi-eye'} me-1`}></i>
                {showPreview ? 'Edit' : 'Preview'}
              </button>
            </div>
            
            {showPreview ? (
              <div className="sb-content-preview">
                {content ? (
                  <ReactMarkdown>{content}</ReactMarkdown>
                ) : (
                  <span className="sb-preview-empty">Nothing to preview</span>
                )}
              </div>
            ) : (
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                <textarea
                  className="sb-textarea"
                  rows={5}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="e.g., My favorite color is blue, I work at Acme Corp, I prefer meetings in the morning..."
                  disabled={saving}
                  autoFocus
                  style={{ flex: 1 }}
                />
                <MicButton
                  onTranscript={(text) => setContent(prev => prev ? `${prev} ${text}` : text)}
                  disabled={saving}
                  size="md"
                />
              </div>
            )}
            <div className="sb-hint">
              Supports markdown: **bold**, *italic*, - lists. Zoomey will remember this when relevant.
            </div>
          </div>

          {/* Quick examples */}
          <div className="kb-examples">
            <span className="kb-examples-label">Examples:</span>
            <button type="button" className="kb-example-chip" onClick={() => setContent("I prefer morning meetings over afternoon ones")}>
              Preferences
            </button>
            <button type="button" className="kb-example-chip" onClick={() => setContent("I work as a software engineer at ")}>
              Work info
            </button>
            <button type="button" className="kb-example-chip" onClick={() => setContent("My birthday is on ")}>
              Personal
            </button>
          </div>
        </Modal.Body>

        <Modal.Footer className="sb-modal-footer">
          <Button 
            variant="secondary" 
            onClick={handleClose} 
            disabled={saving}
            className="sb-btn-cancel"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={saving || !content.trim()}
            className="sb-btn-save"
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-plus-lg me-2"></i>
                Add Memory
              </>
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
